/* =========================================================================
   AddWidgetModal — 3-column widget picker
   Moved from command_center/telemetry/components/add-widget-modal.tsx
   ========================================================================= */

import React, { useState } from 'react';
import { CAT, getSubParams, hydrateCatalogFromCache } from '../utils/telemetry-catalog.utils';
import { getTelemetryVizLabel, getTelemetrySubColor } from '../utils/telemetry-color.utils';
import { mkVizIcon, dotIcon } from '../utils/telemetry-icons.utils';
import { isNoneUnit } from '../utils/telemetry-unit.utils';
import CustomToolTip from '../../CustomToolTip';
import '../styles/telemetry-modal.css';

export interface AddWidgetModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (sub: string, tel: string, viz: string) => void;
  initialSubsystem?: string | null;
  restrictedSubsystem?: string | null;
}

export const AddWidgetModal: React.FC<AddWidgetModalProps> = ({ open, onClose, onAdd, initialSubsystem, restrictedSubsystem }) => {
  if (Object.keys(CAT).length === 0) {
    hydrateCatalogFromCache();
  }
  const subs = Object.keys(CAT);
  const preferredSub = initialSubsystem || restrictedSubsystem;

  // Smart pre-selection with fallback to first subsystem in CAT
  const resolveInitialSub = () => {
    if (preferredSub && CAT[preferredSub]) return preferredSub;
    return subs[0] || null;
  };

  const [selSub, setSelSub] = useState<string | null>(resolveInitialSub());
  const [selTel, setSelTel] = useState<string | null>(null);
  const [selViz, setSelViz] = useState<string | null>(null);
  const [subSearch, setSubSearch] = useState('');
  const [telSearch, setTelSearch] = useState('');

  React.useEffect(() => {
    if (open) {
      setSelSub(resolveInitialSub());
      setSelTel(null);
      setSelViz(null);
      setSubSearch('');
      setTelSearch('');
    }
  }, [open, preferredSub]);

  // Reset telemetry search when active subsystem changes
  const handleSubClick = (sub: string) => {
    setSelSub(sub);
    setSelTel(null);
    setSelViz(null);
    setTelSearch('');
  };

  if (!open) return null;

  const channels = selSub ? Object.entries(getSubParams(selSub)) : [];
  const selDef = selSub && selTel ? getSubParams(selSub)[selTel] : null;

  // Filtered lists based on search queries
  const filteredSubs = subs.filter((sub) => {
    if (!subSearch.trim()) return true;
    const label = CAT[sub]?.label || sub;
    const q = subSearch.toLowerCase();
    return label.toLowerCase().includes(q) || sub.toLowerCase().includes(q);
  });

  const filteredChannels = channels.filter(([key, ch]) => {
    if (!telSearch.trim()) return true;
    const q = telSearch.toLowerCase();
    const label = ch.label || key;
    return (
      label.toLowerCase().includes(q) ||
      key.toLowerCase().includes(q) ||
      (ch.unit && ch.unit.toLowerCase().includes(q)) ||
      (ch.kind && ch.kind.toLowerCase().includes(q))
    );
  });

  // Visualization options
  const isPureKpi = selDef ? (selDef.kind === 'status' || (selDef.allowed.includes('kpi') && selDef.allowed.length === 1)) : false;
  const rawOptions = selDef ? selDef.allowed : [];
  const vizOptions = isPureKpi ? ['kpi'] : rawOptions;

  const handleAdd = () => {
    if (selSub && selTel && selViz) {
      onAdd(selSub, selTel, selViz);
      setSelSub(null); setSelTel(null); setSelViz(null);
      onClose();
    }
  };

  const handleTelClick = (tel: string) => {
    setSelTel(tel);
    const def = selSub ? getSubParams(selSub)[tel] : null;
    if (def) {
      if (def.allowed.length === 1) {
        setSelViz(def.allowed[0]);
      } else {
        setSelViz(null);
      }
    } else {
      setSelViz(null);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSubItemClick = (sub: string) => {
    handleSubClick(sub);
  };

  const handleTelItemClick = (key: string) => {
    handleTelClick(key);
  };

  const handleVizItemClick = (v: string) => {
    setSelViz(v);
  };

  const headerSubLabel = selSub ? (CAT[selSub]?.label || selSub) : '';

  return (
    <div id="telemetry-add-widget-modal-overlay" className="td-modal-overlay" onClick={onClose}>
      <div id="telemetry-add-widget-modal-container" className="td-modal-container" onClick={handleContainerClick}>
        {/* Header */}
        <div id="telemetry-add-widget-modal-header" className="td-modal-header">
          <span id="telemetry-add-widget-modal-title" className="td-modal-title">
            Add Panel {headerSubLabel ? `(${headerSubLabel})` : ''}
          </span>
          <button id="telemetry-add-widget-close-btn" className="td-modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* 3-column picker */}
        <div id="telemetry-add-widget-body-3col" className="td-modal-body-3col">
          {/* Column 1: Subsystems */}
          <div id="telemetry-add-widget-col-subsystems" className="td-modal-col td-modal-col-subsystems">
            <div className="td-modal-col-top">
              <div className="td-modal-col-header">Subsystem</div>
              <div className="td-modal-search-wrap">
                <input
                  id="telemetry-add-widget-sub-search-input"
                  type="text"
                  placeholder="Search..."
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  className="td-modal-search-input"
                />
                {subSearch && (
                  <button className="td-modal-search-clear-btn" onClick={() => setSubSearch('')}>✕</button>
                )}
              </div>
            </div>
            {filteredSubs.length === 0 && (
              <div id="telemetry-add-widget-no-sub-msg" className="td-modal-empty-msg">
                No subsystem found
              </div>
            )}
            {filteredSubs.map((sub) => {
              const isActive = selSub === sub;
              const subLabel = CAT[sub]?.label || sub;

              return (
                <button
                  key={sub}
                  id={`telemetry-add-widget-sub-btn-${sub}`}
                  onClick={() => handleSubItemClick(sub)}
                  className={`td-modal-sub-btn ${isActive ? 'td-modal-sub-btn--active' : ''}`}
                >
                  {dotIcon(getTelemetrySubColor(sub))}
                  <CustomToolTip
                    title={subLabel}
                    placement="top"
                    className="td-modal-sub-label"
                    style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{subLabel}</span>
                  </CustomToolTip>
                </button>
              );
            })}
          </div>

          {/* Column 2: Telemetry Data */}
          <div id="telemetry-add-widget-col-telemetry" className="td-modal-col td-modal-col-telemetry">
            <div className="td-modal-col-top">
              <div className="td-modal-col-header">Telemetry Data</div>
              <div className="td-modal-search-wrap">
                <input
                  id="telemetry-add-widget-tel-search-input"
                  type="text"
                  placeholder="Search..."
                  value={telSearch}
                  onChange={(e) => setTelSearch(e.target.value)}
                  className="td-modal-search-input"
                />
                {telSearch && (
                  <button className="td-modal-search-clear-btn" onClick={() => setTelSearch('')}>✕</button>
                )}
              </div>
            </div>
            {!selSub && (
              <div id="telemetry-add-widget-empty-sub-msg" className="td-modal-empty-msg">
                Select a subsystem
              </div>
            )}
            {selSub && filteredChannels.length === 0 && (
              <div id="telemetry-add-widget-no-tel-msg" className="td-modal-empty-msg">
                No parameters found
              </div>
            )}
            {filteredChannels.map(([key, ch]) => {
              const isActive = selTel === key;
              return (
                <button
                  key={key}
                  id={`telemetry-add-widget-tel-btn-${key}`}
                  onClick={() => handleTelItemClick(key)}
                  className={`td-modal-tel-btn ${isActive ? 'td-modal-tel-btn--active' : ''}`}
                >
                  <CustomToolTip
                    title={ch.label}
                    placement="top"
                    className="td-modal-tel-label"
                    style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{ch.label}</span>
                  </CustomToolTip>
                  <span className="td-modal-unit-tag">
                    {!isNoneUnit(ch.unit) && ch.unit}
                    <span className="td-modal-kind-tag">
                      {ch.kind}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Column 3: Visualization */}
          <div id="telemetry-add-widget-col-viz" className="td-modal-col td-modal-col-visualization">
            <div className="td-modal-col-top">
              <div className="td-modal-col-header">Visualization</div>
            </div>
            {vizOptions.length === 0 && (
              <div id="telemetry-add-widget-empty-tel-msg" className="td-modal-empty-msg">
                Select telemetry data
              </div>
            )}
            <div className="td-modal-viz-grid">
              {vizOptions.map((v) => {
                const isActive = selViz === v;
                return (
                  <button
                    key={v}
                    id={`telemetry-add-widget-viz-btn-${v}`}
                    onClick={() => handleVizItemClick(v)}
                    className={`td-modal-viz-btn ${isActive ? 'td-modal-viz-btn--active' : ''}`}
                  >
                    {mkVizIcon(v, 18)}
                    {getTelemetryVizLabel(v)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer / Summary bar */}
        <div id="telemetry-add-widget-modal-footer" className="td-modal-footer">
          <span id="telemetry-add-widget-summary" className="td-modal-summary">
            {selSub && (CAT[selSub]?.label || selSub)}
            {selTel && selDef && <> · {selDef.label}</>}
            {selViz && <> · {getTelemetryVizLabel(selViz)}</>}
          </span>
          <button
            id="telemetry-add-widget-submit-btn"
            className="td-modal-submit-btn"
            onClick={handleAdd}
            disabled={!selSub || !selTel || !selViz}
          >
            Add to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddWidgetModal;
